import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const LINKS = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

export const LandingNav = () => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/80"
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[4.5rem] md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo-link" onClick={() => setOpen(false)}>
          <img src="/brand/fmark-512.png" alt="FulfillFlow" className="h-10 w-10 object-contain" />
          <span className="lp-display text-xl font-semibold tracking-tight">
            <span className="text-[#0b1530]">Fulfill</span>
            <span className="lp-gradient-text">Flow</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9 text-[15px] font-medium text-slate-600">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `transition-colors hover:text-blue-600 ${isActive ? "text-blue-600" : ""}`
              }
              data-testid={`nav-${l.label.toLowerCase()}-link`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[15px] font-semibold px-6 py-2.5 transition-colors shadow-lg shadow-blue-600/25"
              data-testid="nav-dashboard-btn"
            >
              Open Dashboard <ArrowRight size={16} strokeWidth={2} />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-[15px] font-medium text-slate-700 hover:text-blue-600 transition-colors px-3 py-2"
                data-testid="nav-signin-btn"
              >
                Sign In
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[15px] font-semibold px-6 py-2.5 transition-colors shadow-lg shadow-blue-600/25"
                data-testid="nav-demo-btn"
              >
                Request Demo
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 text-slate-700"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-t border-slate-200 bg-white"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="py-3 text-base font-medium text-slate-700 hover:text-blue-600 border-b border-slate-100"
                >
                  {l.label}
                </NavLink>
              ))}
              {isAuthenticated ? (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="mt-3 inline-flex justify-center items-center gap-2 rounded-full bg-blue-600 text-white font-semibold px-6 py-3">
                  Open Dashboard <ArrowRight size={16} />
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="py-3 text-base font-medium text-slate-700" data-testid="nav-mobile-signin">
                    Sign In
                  </Link>
                  <Link to="/contact" onClick={() => setOpen(false)} className="mt-1 mb-2 inline-flex justify-center items-center gap-2 rounded-full bg-blue-600 text-white font-semibold px-6 py-3">
                    Request Demo
                  </Link>
                </>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
